import {
  MessageAppend,
  SignRequest,
  TransactionButtonProps,
  TransactionContainerProps,
} from '@bitte-ai/types';

import { formatEther } from 'viem';

import { createDeclineTransactionMessage } from '../../../lib/transaction-messages';
import { shortenString } from '../../../lib/utils';

import { useState } from 'react';
import { useEVMTransaction } from '../../../hooks/useEvmTransaction';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { decodeHexMessage } from '../../../lib/evm/decode';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../ui/accordion';
import { Button } from '../../ui/button';
import { CardFooter, CardHeader } from '../../ui/card';
import DefaultTxApproveButton from '../default-components/DefaultTxApproveButton';
import DefaultTxContainer from '../default-components/DefaultTxContainer';
import DefaultTxDeclineButton from '../default-components/DefaultTxDeclineButton';
import { CopyStandard } from './../CopyStandard';
import LoadingMessage from './../LoadingMessage';
import { TransactionDetail } from './TransactionDetail';
import { TransactionResult } from './TransactionResult';

export interface EvmTxCardProps {
  evmData?: SignRequest;
  messageBackgroundColor: string;
  borderColor: string;
  textColor: string;
  customTxContainer?: React.ComponentType<TransactionContainerProps>;
  customApproveTxButton?: React.ComponentType<TransactionButtonProps>;
  customDeclineTxButton?: React.ComponentType<TransactionButtonProps>;
  append: MessageAppend;
  messageId: string;
}

export const EvmTxCard = ({
  evmData,
  messageBackgroundColor,
  borderColor,
  textColor,
  customTxContainer: TxContainer = DefaultTxContainer,
  customApproveTxButton: ApproveButton = DefaultTxApproveButton,
  customDeclineTxButton: DeclineButton = DefaultTxDeclineButton,
  append,
  messageId,
}: EvmTxCardProps) => {
  const { width } = useWindowSize();
  const isMobile = !!width && width < 640;
  const {
    isLoading,
    errorMsg,
    transaction,
    handleSmartAction,
    setErrorMsg,
    network,
    evmAddress,
    setTransaction,
  } = useEVMTransaction({ evmData, append, messageId });

  const [isDeclined, setIsDeclined] = useState(false);

  if (!evmData)
    return (
      <p className="bitte-my-4 bitte-overflow-auto bitte-text-center">
        Unable to create evm transaction.
      </p>
    );

  const loadingIndicator = (
    <div>
      <LoadingMessage color={textColor} />
      <p className="bitte-text-center bitte-text-sm">
        {evmData.method === 'eth_sendTransaction'
          ? 'Transaction'
          : 'Signature request'}{' '}
        for{' '}
        {evmData.method === 'eth_sendTransaction'
          ? shortenString(evmData.params[0].to, 8)
          : evmData.method === 'personal_sign' || evmData.method === 'eth_sign'
            ? shortenString(evmData.params[0], 8)
            : 'signature request'}{' '}
        in progress
      </p>
    </div>
  );

  const declineTransaction = async () => {
    await append(createDeclineTransactionMessage(messageId));
    setIsDeclined(true);
  };
  const { method, chainId, params } = evmData;
  return (
    <TxContainer
      style={{
        backgroundColor: messageBackgroundColor,
        borderColor,
        textColor,
      }}
    >
      <CardHeader
        className="bitte-border-b bitte-p-4 bitte-text-center bitte-md:p-6"
        style={{ borderColor: borderColor }}
      >
        <p className="bitte-text-xl bitte-font-semibold">EVM Transaction</p>
      </CardHeader>
      <div>
        {
          <div className="bitte-p-6">
            <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
              <TransactionDetail
                label="Chain ID"
                value={shortenString(chainId.toString(), isMobile ? 13 : 21)}
              />
              <TransactionDetail label="Network" value={network?.name || ''} />
              {method === 'eth_sendTransaction' && (
                <Accordion
                  type="single"
                  collapsible
                  defaultValue="transaction-0"
                >
                  {params.map((transaction, index) => (
                    <AccordionItem
                      key={`tx-${typeof transaction === 'object' ? transaction.to : ''}-${index}`}
                      value={`transaction-${index}`}
                    >
                      <AccordionTrigger className="bitte-pt-0">
                        <div className="bitte-flex bitte-items-center bitte-justify-between bitte-text-sm">
                          <p>Transaction {index + 1}</p>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="bitte-flex bitte-flex-col bitte-gap-6">
                        {typeof transaction === 'object' && transaction.to && (
                          <TransactionDetail
                            label="To"
                            className="bitte--mr-2.5"
                            value={
                              <CopyStandard
                                text={transaction.to}
                                textSize="sm"
                                charSize={isMobile ? 7 : 12}
                              />
                            }
                          />
                        )}
                        <TransactionDetail
                          label="Value"
                          value={
                            typeof transaction === 'object' && transaction.value
                              ? formatEther(BigInt(transaction.value))
                              : '0'
                          }
                        />
                        <TransactionDetail
                          label="Data"
                          value={
                            <CopyStandard
                              text={
                                typeof transaction === 'object'
                                  ? transaction.data || '0x'
                                  : '0x'
                              }
                              textSize="sm"
                              charSize={isMobile ? 10 : 15}
                            />
                          }
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
              {method === 'personal_sign' && (
                <div className="bitte-p-6">
                  <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
                    <TransactionDetail
                      label="Message"
                      value={
                        <CopyStandard
                          text={decodeHexMessage(params[0])}
                          textSize="sm"
                        />
                      }
                    />
                  </div>
                </div>
              )}
              {method === 'eth_sign' && (
                <div className="bitte-p-6">
                  <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
                    <TransactionDetail
                      label="Message"
                      value={
                        <CopyStandard
                          text={decodeHexMessage(params[1])}
                          textSize="sm"
                        />
                      }
                    />
                  </div>
                </div>
              )}
              {(method === 'eth_signTypedData' ||
                method === 'eth_signTypedData_v4') && (
                <div className="bitte-p-6">
                  <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
                    <TransactionDetail
                      label="Typed Data"
                      value={
                        <div className="bitte-max-h-40 bitte-overflow-auto">
                          <pre className="bitte-text-xs bitte-whitespace-pre-wrap">
                            {JSON.stringify(JSON.parse(params[1]), null, 2)}
                          </pre>
                        </div>
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        }
      </div>

      {errorMsg || (transaction?.status === 'error' && transaction?.error) ? (
        <div className="bitte-flex bitte-flex-col bitte-items-center bitte-gap-4 bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm">
          <p className="bitte-text-red-300">
            {transaction?.status === 'error' && transaction?.error
              ? transaction.error
              : `An error occurred trying to execute your transaction: ${errorMsg}.`}
          </p>
          <Button
            className="bitte-w-1/2"
            variant="outline"
            onClick={() => {
              setErrorMsg('');
              // Clear error state from transaction if needed
              if (transaction?.status === 'error') {
                setTransaction((prev) =>
                  prev
                    ? {
                        ...prev,
                        error: undefined,
                      }
                    : null,
                );
              }
            }}
          >
            Dismiss
          </Button>
        </div>
      ) : isLoading ? (
        loadingIndicator
      ) : null}

      {isDeclined ? (
        <div className="bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm">
          Transaction declined!
        </div>
      ) : null}

      {transaction?.txHash ? (
        <TransactionResult
          result={{
            evm: { txHash: transaction.txHash, chainId: evmData.chainId },
          }}
          accountId={evmAddress || ''}
          textColor={textColor}
          success={transaction.status === 'success'}
        />
      ) : null}

      {!isLoading && !errorMsg && !transaction?.txHash && !isDeclined ? (
        <CardFooter className="bitte-flex bitte-items-center bitte-gap-6">
          <>
            <DeclineButton onClick={declineTransaction} disabled={isLoading} />
            <ApproveButton
              onClick={handleSmartAction}
              disabled={isLoading}
              isLoading={isLoading}
            />
          </>
        </CardFooter>
      ) : null}
    </TxContainer>
  );
};
